import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { AddressComponents } from "projects/common/src/lib/models/address-comments.model";
import { getDefaultTimezone, timezones } from "projects/common/src/lib/utils/timezones";
import {
  AdditionalBusinessInfoForm,
  BankInformationForm,
  DBAForm,
  DocumentType,
  LegalForm,
  NuveiApplicationFormUpdate,
  NuveiApplicationRequest,
  SalesProfileForm
} from "../../models/nuvei-application.model";
import { NuveiService, SavedNuveiApplicationForm, SubmitApplicationErrors } from "../../services/nuvei.service";
import { BusinessService } from "../../services/business.service";
import { map, shareReplay, startWith, take } from "rxjs/operators";
import { camelCaseToSnake, timestampsToDate, UtilsService } from "../../../../../common/src/lib/services";
import { BehaviorSubject, combineLatest } from "rxjs";
import {
  additionalBusinessInfoFormProgress, bankInformationFormProgress, businessOwnersFormProgress,
  dbaFormProgress,
  documentsProgress,
  formProgress,
  legalFormProgress, salesProfileFormProgress
} from "./nuvei-form-progress";
import { DOCUMENT, Location } from "@angular/common";
import {
  BusinessOwnersFormErrors,
  ExistingDocuments,
  FormErrors,
  validateAdditionalBusinessInfoForm,
  validateBankInformationForm,
  validateBusinessOwnersForm,
  validateDBAForm,
  validateDocumentFiles,
  validateLegalForm,
  validateSalesProfileForm
} from "../../helpers/nuvei/nuvei-form-validation";
import { NuveiApplicationComponentFormName } from "./nuvei-application-step";
import { UploadedFile } from "./nuvei-application-documents/nuvei-application-documents.component";
import { serverToFrontTranslation } from "../../../../../common/src/lib/services/supabase.service";
import { deepEqual } from "../../helpers/common";
import { ModalBehavior, ModalsService } from "../../../../../common/src/lib/services/modals.service";
import {
  ConfirmationDialog
} from "../../../../../common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { Router } from "@angular/router";
import { UsersService } from "../../services/users.service";
import { UserProfile } from "../../../../../common/src/lib/models/user-profile.model";
import { BusinessProfile } from "../../../../../common/src/lib/models/business-profile.model";

type OwnershipType = NuveiApplicationRequest['MerchantBusinessInformation']['OwnershipType'];
type Zoning = NuveiApplicationRequest['SiteSurveyInformation']['MerchantsLocaleZoning'];
type Locale = NuveiApplicationRequest['SiteSurveyInformation']['MerchantLocale'];
type SquareFootage = NuveiApplicationRequest['SiteSurveyInformation']['MerchantSquareFootage'];
type AssociatedBankAddress = NuveiApplicationRequest['BankInformation']['BankAccount'];
type Timezone = typeof timezones[number];

@Component({
  selector: 'app-nuvei-application',
  templateUrl: './nuvei-application.component.html',
  styleUrls: ['./nuvei-application.component.scss']
})
export class NuveiApplicationComponent implements OnInit {

  legalForm = new FormGroup({
    businessName: new FormControl<string>('', { nonNullable: true }),
    ownershipType: new FormControl<OwnershipType>('' as OwnershipType, { nonNullable: true }),
    address: new FormControl<AddressComponents | null>(null),
    unit: new FormControl<string>('', { nonNullable: true }),
    mailingAddressSameAsAddress: new FormControl<boolean>(true, { nonNullable: true }),
    mailingAddress: new FormControl<AddressComponents | null>(null),
    mailingAddressUnit: new FormControl<string>('', { nonNullable: true }),
    mailingAttention: new FormControl<string>('', { nonNullable: true }),
    businessPhoneNumber: new FormControl<string>('', { nonNullable: true }),
    supportPhoneNumberSameAsBusinessPhoneNumber: new FormControl<boolean>(true, { nonNullable: true }),
    supportPhoneNumber: new FormControl<string>('', { nonNullable: true }),
    contactPhoneNumberSameAsBusinessPhoneNumber: new FormControl<boolean>(true, { nonNullable: true }),
    contactPhoneNumber: new FormControl<string>('', { nonNullable: true }),
    mobilePhoneNumberSameAsBusinessPhoneNumber: new FormControl<boolean>(true, { nonNullable: true }),
    mobilePhoneNumber: new FormControl<string>('', { nonNullable: true }),
    businessEmail: new FormControl<string>('', { nonNullable: true }),
    contactEmailSameAsBusinessEmail: new FormControl<boolean>(true, { nonNullable: true }),
    contactEmail: new FormControl<string>('', { nonNullable: true }),
    creationDate: new FormControl<Date>(null as any, { nonNullable: true }),
    federalTaxID: new FormControl<string>('', { nonNullable: true }),
    currentPaymentProcessor: new FormControl<string>('', { nonNullable: true }),
  });

  dbaForm = new FormGroup({
    businessName: new FormControl<string>('', { nonNullable: true }),
    address: new FormControl<AddressComponents | null>(null),
    unit: new FormControl<string>('', { nonNullable: true }),
    phoneNumber: new FormControl<string>('', { nonNullable: true }),
    creationDate: new FormControl<Date>(null as any, { nonNullable: true }),
    timezone: new FormControl<Timezone>(getDefaultTimezone() as any, { nonNullable: true }),
  });

  additionalBusinessInfoForm = new FormGroup({
    website: new FormControl<string>('', { nonNullable: true }),
    businessType: new FormControl<string>('', { nonNullable: true }),
    description: new FormControl<string>('', { nonNullable: true }),
    zoning: new FormControl<Zoning>('' as Zoning, { nonNullable: true }),
    locale: new FormControl<Locale>('' as Locale, { nonNullable: true }),
    squareFootage: new FormControl<SquareFootage>('' as SquareFootage, { nonNullable: true }),
    hasRefundPolicy: new FormControl<boolean>(false, { nonNullable: true }),
    refundPolicy: new FormControl<string>('', { nonNullable: true }),
  });

  salesProfileForm = new FormGroup({
    avgVolumePerMonth: new FormControl<number>(0, { nonNullable: true }),
    avgTicketSize: new FormControl<number>(0, { nonNullable: true }),
    highTicket: new FormControl<number>(0, { nonNullable: true }),
    expectedAMEXPercentage: new FormControl<number>(0, { nonNullable: true }),
  });

  businessOwnersForm = new FormGroup({
    owners: new FormArray([
        this.createOwnerDetailsFormGroup()
    ]),
    guarantor: new FormControl<number>(0, { nonNullable: true }),
  });

  bankInformationForm = new FormGroup({
    bankTransit: new FormControl<string>('', { nonNullable: true }),
    bankAccountNumber: new FormControl<string>('', { nonNullable: true }),
    associatedAddress: new FormControl<AssociatedBankAddress>('' as any, { nonNullable: true }),
  });

  documentsProgressSubject = new BehaviorSubject<{
    documents: ExistingDocuments,
    hasPreviousPaymentProcessorInForm?: boolean
  }>({ documents: {}, hasPreviousPaymentProcessorInForm: false });
  progress$ = combineLatest([
    this.documentsProgressSubject,
    this.legalForm.valueChanges.pipe(startWith(this.legalForm.value)),
    this.dbaForm.valueChanges.pipe(startWith(this.dbaForm.value)),
    this.additionalBusinessInfoForm.valueChanges.pipe(startWith(this.additionalBusinessInfoForm.value)),
    this.salesProfileForm.valueChanges.pipe(startWith(this.salesProfileForm.value)),
    this.businessOwnersForm.valueChanges.pipe(startWith(this.businessOwnersForm.value)),
    this.bankInformationForm.valueChanges.pipe(startWith(this.bankInformationForm.value))
  ]).pipe(
      map(values => {
        const r = 100 - Math.floor(formProgress(
            this.documentsProgressSubject.value,
            this.legalForm.value,
            this.dbaForm.value,
            this.additionalBusinessInfoForm.value,
            this.salesProfileForm.value,
            this.businessOwnersForm.value,
            this.bankInformationForm.value,
        ) * 100);
        return r;
      }),
      startWith(0),
      shareReplay({ bufferSize: 1, refCount: true }),
  );

  step = -1;
  get stepInfo(): { title: string, summary?: string} {
    const result = [
      {
        title: 'Required Documents',
        summary: 'To complete your application, you must provide documents that verify your business identity, banking details, and (optionally) payment history. Ensure all documents are clear, up-to-date, and match the business information provided in the application to ensure compliance.'
      },
      {
        title: 'Legal business information',
        summary: 'Provide the official details of your business as registered with the appropriate government agencies. Ensure that the business name, address, and other details match those on your legal registration documents, tax filings, and business licenses. These details help verify your business identity and ensure compliance with regulatory requirements.'
      },
      {
        title: 'Business display information (DBA)',
        summary: 'This section is prefilled with the information you provided on our platform. If needed, you can update the details manually or press the "Same as Legal" button to automatically fill it with the information from the previous step. DBA (Doing Business As) is the public-facing details of your business, including the name, address, and contact information used for customer interactions. Ensure that this information aligns with how your business is advertised and presented to the public.'
      },
      {
        title: 'Additional business information',
        summary: 'This section helps define the services your business offers and provides context on its operations and environment. It includes details about your business activities, location characteristics, and policies, ensuring clarity for compliance, customer expectations, and service alignment.'
      },
      {
        title: 'Sales profile',
        summary: 'This section helps assess your business’s transaction patterns and processing needs. These figures can be based on historical data or projections for new businesses. Accurate estimates ensure proper payment processing and account setup.'
      },
      {
        title: 'Business owner(s) details',
        summary: 'This section captures key information about the owner, verifying their identity and role in the business. You can add up to 4 owners'
      },
      {
        title: 'Bank information',
        summary: 'This section captures the necessary details for processing payments and depositing funds into your business account. Ensuring accuracy here helps prevent transaction issues and delays, allowing for smooth and reliable payment operations.'
      },
      {
        title: 'Application data',
        summary: 'Please take a moment to carefully review all the details you’ve provided.'
      },
      {
        title: 'Document preview',
        summary: 'Please take a moment to carefully review all the details you’ve provided.'
      }
    ][this.step];
    if(this.step === 8) {
      if(this.summeryWarn)
        result.summary = 'Waiting for guarantor signature';
      else if(this.form.status === 'pending')
        result.summary = 'Thank you for submitting your application! We\'ve forwarded your details for review and will notify you once we receive an update.';
      else if(this.businessOwnersForm.value.guarantor !== 0 && this.form.signatures.guarantor?.submitted) {}
      result.summary = 'The guarantor has signed the document. Please take a moment to carefully review all the details you’ve provided.';
    }
    return result;
  }

  form!: SavedNuveiApplicationForm;
  missingDocuments: DocumentType[] = [];
  uploadedFiles: {
    [T in DocumentType]?: UploadedFile
  } = {};
  businessName!: string;

  isMobile = this.utilsService.isMobile();

  submittingDocument = false;

  get waitingForGuarantor() {
    return !this.form.signatures.guarantor?.submitted ?? false;
  }

  get summeryWarn() {
    if(this.businessOwnersForm.value.guarantor !== 0 && this.step === 8) {
      if(this.waitingForGuarantor)
        return true;
    }
    return false;
  }

  constructor(
     @Inject(DOCUMENT) private document: Document,
     private window: Window,
     private location: Location,
     private businessService: BusinessService,
     private utilsService: UtilsService,
     private usersService: UsersService,
     private nuveiService: NuveiService,
     private zone: NgZone,
     private router: Router,
     private modalsService: ModalsService,
  ) {}

  async ngOnInit() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    this.businessName = business.businessName;
    try {
      this.form = await this.nuveiService.getApplicationForm(business.businessId);
      await this.initForm(business);
    } catch (e) {
      console.log(e);
      this.router.navigate(['/lobby']);
    }
  }

  async initForm(business: BusinessProfile) {

    const currentUser = (await this.usersService.currentUser$.pipe(take(1)).toPromise())!;
    if(this.form.formDocument?.documentExpiration)
      this.form.formDocument!.documentExpiration = new Date(this.form.formDocument!.documentExpiration);
    const keys = Object.keys(this.form.documents) as DocumentType[];
    if(keys.length > 0) {
      this.uploadedFiles = {};
      const existingDocuments: ExistingDocuments = {};
      for (const key of keys) {
        existingDocuments[key] = true;
        const file = this.form.documents[key]!;
        this.uploadedFiles[key] = {
          name: file.fileName,
          mimeType: file.mimeType,
          size: file.fileSize,
          ts: file.timestamp,
          downloadUrl: file.downloadUrl,
        };
      }
      const hasPreviousPaymentProcessorInForm = !!this.form.legalForm?.currentPaymentProcessor && this.form.legalForm?.currentPaymentProcessor !== '';
      this.documentsProgressSubject.next({
        documents: existingDocuments,
        hasPreviousPaymentProcessorInForm
      });
    }
    const patchForm = <T extends NuveiApplicationComponentFormName>(key: T) => {
      const value = this.form[key];
      if(value) {
        this[key].patchValue(value);
      }
    }
    if(this.form.legalForm)
      patchForm('legalForm');
    else
      this.initLegal(business);

    if(this.form.dbaForm)
      patchForm('dbaForm');
    else
      this.initDBAData(business);
    patchForm('additionalBusinessInfoForm');
    patchForm('salesProfileForm');

    const firstOwnerEmailControl = this.businessOwnersForm.controls.owners.controls[0].controls.email;
    if(this.form.businessOwnersForm) {
      const form = this.form.businessOwnersForm;
      if(form.owners) {
        this.businessOwnersForm.controls.owners.controls[0].patchValue(form.owners[0]);
        if(form.owners.length > 1) {
          for(let i = 1; i < form.owners.length; i++)
            this.addOwner(form.owners[i]);
        }
      }
      if(form.guarantor)
        this.businessOwnersForm.controls.guarantor.setValue(form.guarantor);
      firstOwnerEmailControl.setValue(currentUser.email);
    } else {
      this.initOwnerData(currentUser);
    }
    patchForm('bankInformationForm');

    this.navigateToFirstUnfinished();
  }

  initLegal(business: BusinessProfile) {
    this.legalForm.patchValue({
      businessName: business.businessName,
      address: business.address,
      businessPhoneNumber: business.phoneNumber,
    });
  }

  initDBAData(business: BusinessProfile) {
    this.dbaForm.patchValue({
      businessName: business.businessName,
      address: business.address,
      phoneNumber: business.phoneNumber,
    });
  }

  initOwnerData(currentUser: UserProfile) {
    const ownerData: any = {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
    if(currentUser.directPhoneNumber)
      ownerData['directPhoneNumber'] = currentUser.directPhoneNumber;
    this.businessOwnersForm.controls.owners.controls[0].patchValue(ownerData);
  }

  onFilesChange(files: { [T in DocumentType]?: UploadedFile }) {
    this.uploadedFiles = files;
    const hasPreviousPaymentProcessor = !!this.legalForm.value.currentPaymentProcessor && this.legalForm.value.currentPaymentProcessor !== '';
    this.documentsProgressSubject.next({
      documents: {
        proofOfBusiness: !!this.uploadedFiles.proofOfBusiness,
        voidCheck: !!this.uploadedFiles.voidCheck,
        previousPaymentProcessor: !!this.uploadedFiles.previousPaymentProcessor,
      },
      hasPreviousPaymentProcessorInForm: hasPreviousPaymentProcessor
    });
  }

  createOwnerDetailsFormGroup() {
    return new FormGroup({
      firstName: new FormControl<string>('', { nonNullable: true }),
      lastName: new FormControl<string>('', { nonNullable: true }),
      phoneNumber: new FormControl<string>('', { nonNullable: true }),
      email: new FormControl<string>('', { nonNullable: true }),
      title: new FormControl<string>('', { nonNullable: true }),
      ownershipPercentage: new FormControl<number>(0, { nonNullable: true }),
      citizenship: new FormControl<string>('', { nonNullable: true }),
      birthDate: new FormControl<Date>(new Date(), { nonNullable: true }),
      ssn: new FormControl<string>('', { nonNullable: true }),
      address: new FormControl<AddressComponents | null>(null),
      unit: new FormControl<string>('', { nonNullable: true }),
    });
  }

  navigateToFirstUnfinished() {
    if(this.form.signatures?.owner?.submitted) {
      this.step = 8;
      return;
    }

    let stepProgress = documentsProgress(this.documentsProgressSubject.value.documents, this.documentsProgressSubject.value.hasPreviousPaymentProcessorInForm);
    if(stepProgress.errors > 0) {
      this.step = 0;
      return;
    }

    stepProgress = legalFormProgress(this.legalForm.value);
    if(stepProgress.errors > 0) {
      this.step = 1;
      return;
    }

    stepProgress = dbaFormProgress(this.dbaForm.value);
    if(stepProgress.errors > 0) {
      this.step = 2;
      return;
    }

    stepProgress = additionalBusinessInfoFormProgress(this.additionalBusinessInfoForm.value);
    if(stepProgress.errors > 0) {
      this.step = 3;
      return;
    }

    stepProgress = salesProfileFormProgress(this.salesProfileForm.value);
    if(stepProgress.errors > 0) {
      this.step = 4;
      return;
    }

    stepProgress = businessOwnersFormProgress(this.businessOwnersForm.value);
    if(stepProgress.errors > 0) {
      this.step = 5;
      return;
    }

    stepProgress = bankInformationFormProgress(this.bankInformationForm.value);
    if(stepProgress.errors > 0) {
      this.step = 6;
      return;
    }

    this.step = 7;
  }

  addOwner(data?: any) {
    const group = this.createOwnerDetailsFormGroup();
    if(data)
      group.patchValue(data);
    this.businessOwnersForm.controls.owners.push(group);
  }

  back() {
    this.zone.run(() => {
      this.step--;
    });
  }

  close() {
    this.location.back();
  }

  async updateForm(formName: keyof typeof this) {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    const form = this[formName] as FormGroup;
    const data: NuveiApplicationFormUpdate = {
      in_business_id: business.businessId,
      in_legal_form: null,
      in_dba_form: null,
      in_additional_business_info_form: null,
      in_sales_profile_form: null,
      in_business_owners_form: null,
      in_bank_information_form: null
    };
    const key = 'in_' + camelCaseToSnake(formName as string);
    data[key as keyof NuveiApplicationFormUpdate] = form.value;
    await this.nuveiService.updateForm(data);
    this.form.signatures = {};
  }

  async revokeOwnerSignature(approval: string) {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    this.zone.run(() => this.form.signatures = {} );
    const result = await this.nuveiService.revokeApproval(business.businessId, approval, 'signature');
    this.zone.run(() => {
      if(result)
        this.form.formDocument!.documentUrl = result;
    });
  }

  onFormDocumentUrlUpdate(url: string) {
    this.form.formDocument = { documentUrl: url };
  }

  async nextStep() {
    const validationFunction = [
      this.validateDocuments,
      this.validateLegalForm,
      this.validateDBAForm,
      this.validateAdditionalBusinessInfoForm,
      this.validateSalesProfileForm,
      this.validateBusinessOwnersForm,
      this.validateBankInformationForm,
    ][this.step].bind(this);

    if(this.step > 0 && this.step < 7)
      this.updateForm([
          'legalForm',
          'dbaForm',
          'additionalBusinessInfoForm',
          'salesProfileForm',
          'businessOwnersForm',
          'bankInformationForm'
      ][this.step-1]! as keyof this);

    const formValid = validationFunction();
    if(formValid) {
      const progress = await this.progress$.pipe(take(1)).toPromise();
      if(progress === 100) {
        this.zone.run(() => this.step = 7);
      } else {
        this.zone.run(() => {
          if(this.step === 6)
            this.step = 0;
          else
            this.step++;
        });
      }
    }
  }

  validateDocuments(e?: FormErrors<ExistingDocuments>) {
    const hasPreviousPaymentProcessor = !!this.legalForm.value.currentPaymentProcessor && this.legalForm.value.currentPaymentProcessor !== '';
    const errors = e ?? validateDocumentFiles({
      proofOfBusiness: !!this.uploadedFiles.proofOfBusiness,
      voidCheck: !!this.uploadedFiles.voidCheck,
      previousPaymentProcessor: !!this.uploadedFiles.previousPaymentProcessor,
    }, hasPreviousPaymentProcessor);

    const hasErrors = Object.values(errors).length > 0;
    if(hasErrors) {
      this.missingDocuments = Object.keys(errors) as DocumentType[];
      const id = (['proofOfBusiness', 'voidCheck', 'previousPaymentProcessor'] as DocumentType[])
          .find(id => typeof errors[id] === 'string')!;
      this.scrollToControl('document_'+id);
    }
    return !hasErrors;
  }

  validateLegalForm(errors?: FormErrors<LegalForm>) {
    this.resetFormErrors('legalForm');
    if(!errors)
      errors = validateLegalForm(this.legalForm.value);
    const hasErrors = Object.values(errors).length > 0;
    if(hasErrors) {
      this.setErrors(errors, this.legalForm);
      this.scrollToControl();
    }
    return !hasErrors;
  }

  validateDBAForm(errors?: FormErrors<DBAForm>) {
    this.resetFormErrors('dbaForm');
    if(!errors)
      errors = validateDBAForm(this.dbaForm.value);
    const hasErrors = Object.values(errors).length > 0;
    if(hasErrors) {
      this.setErrors(errors, this.dbaForm);
      this.scrollToControl();
    }
    return !hasErrors;
  }

  validateAdditionalBusinessInfoForm(errors?: FormErrors<AdditionalBusinessInfoForm>) {
    this.resetFormErrors('additionalBusinessInfoForm')
    if(!errors)
      errors = validateAdditionalBusinessInfoForm(this.additionalBusinessInfoForm.value);
    const hasErrors = Object.values(errors).length > 0;
    if(hasErrors) {
      this.setErrors(errors, this.additionalBusinessInfoForm);
      this.scrollToControl();
    }
    return !hasErrors;
  }

  validateSalesProfileForm(errors?: FormErrors<SalesProfileForm>) {
    this.resetFormErrors('salesProfileForm')
    if(!errors)
      errors = validateSalesProfileForm(this.salesProfileForm.value);
    const hasErrors = Object.values(errors).length > 0;
    if(hasErrors) {
      this.setErrors(errors, this.salesProfileForm);
      this.scrollToControl();
    }
    return !hasErrors;
  }

  validateBusinessOwnersForm(errors?: BusinessOwnersFormErrors) {
    this.resetFormErrors('businessOwnersForm');
    if(!errors)
      errors = validateBusinessOwnersForm(this.businessOwnersForm.value);
    let hasErrors = false;
    for(let i = 0; i < errors.owners.length; i++) {
      if(errors.owners[i]) {
        this.setErrors(errors.owners[i], this.businessOwnersForm.controls.owners.controls[i]);
        hasErrors = true;
      }
    }
    if(!!errors.guarantor) {
      this.setErrors({ guarantor: errors.guarantor }, this.businessOwnersForm);
    }

    if(hasErrors) {
      this.scrollToControl();
    }
    return !hasErrors;
  }

  validateBankInformationForm(errors?: FormErrors<BankInformationForm>) {
    this.resetFormErrors('bankInformationForm')
    if(!errors)
      errors = validateBankInformationForm(this.bankInformationForm.value);
    const hasErrors = Object.values(errors).length > 0;
    if(hasErrors) {
      this.setErrors(errors, this.bankInformationForm);
      this.scrollToControl();
    }
    return !hasErrors;
  }

  scrollToControl(byId?: string) {
    this.window.blur();
    setTimeout((() => {
      // This part is to force UI changes if the element is already in view
      const formElement = this.document.getElementsByTagName('form').item(0);
      if(formElement)
        formElement.scrollTop += 1;

      const element = byId
          ? this.document.getElementById(byId)
          : this.document.getElementsByClassName('mat-form-field-invalid').item(0);
      if(element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }).bind(this), 50);
  }

  setErrors<FG extends FormGroup>(errors: FormErrors<FG['value']>, form: FG) {
    const keys = Object.keys(errors) as (keyof typeof errors)[];
    for(const field of keys) {
      const error = errors[field];
      if(typeof error === 'string') {
        const control = (form as FormGroup).get(field as string)!
        control.setErrors({
          [error]: true
        });
        control.markAsTouched();
      }
    }
  }

  resetFormErrors(formName: NuveiApplicationComponentFormName) {
    const form = this[formName];
    form.setErrors(null);
    form.markAllAsTouched();
    if(formName === 'businessOwnersForm') {
      for(const group of (form as this['businessOwnersForm']).controls.owners.controls) {
        group.setErrors(null);
        for(const field of Object.keys(group.controls)) {
          group.controls[field as keyof typeof group.controls].setErrors(null);
          group.markAllAsTouched();
        }
      }
    }
  }


  skipDocuments() {
    this.zone.run(() => {
      this.step = 1;
    });
  }

  async submit() {
    this.submittingDocument = true;
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    const dataSameAsServer = await this.compareServerFormData(business.businessId);
    if(!dataSameAsServer)
      return;

    try {
      await this.nuveiService.submitApplication(business.businessId, this.form.formDocument!.documentApproval!);
      const updatedForm = await this.nuveiService.getApplicationForm(business.businessId);
      this.zone.run(() => {
        this.form = updatedForm;
      });
    } catch (e: any) {
      const error = await e.context.json();
      if(error.message === "\"Owner not found\"") {
        this.checkServerDataMismatch();
      } else {
        const errors = serverToFrontTranslation(error) as SubmitApplicationErrors;
        if(errors) {
          this.handleFormErrors(errors);
        }
      }
    }

    this.submittingDocument = false;
  }

  async checkServerDataMismatch() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    return this.compareServerFormData(business.businessId);
  }

  async handleFormErrors(errors: SubmitApplicationErrors) {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    if(errors.files) {
      this.step = 0;
    } else if(errors.legalForm) {
      const dataSameAsServer = await this.compareServerFormData(business.businessId);
      if(dataSameAsServer) {
        this.step = 1;
        this.validateLegalForm(errors.legalForm);
      }
    } else if(errors.dbaForm) {
      const dataSameAsServer = await this.compareServerFormData(business.businessId);
      if(dataSameAsServer) {
        this.step = 2;
        this.validateDBAForm(errors.dbaForm);
      }
    } else if(errors.additionalBusinessInfoForm) {
      const dataSameAsServer = await this.compareServerFormData(business.businessId);
      if(dataSameAsServer) {
        this.step = 3;
        this.validateAdditionalBusinessInfoForm(errors.additionalBusinessInfoForm);
      }
    } else if(errors.salesProfileForm) {
      const dataSameAsServer = await this.compareServerFormData(business.businessId);
      if(dataSameAsServer) {
        this.step = 4;
        this.validateSalesProfileForm(errors.salesProfileForm);
      }
    } else if(errors.businessOwnersForm) {
      const dataSameAsServer = await this.compareServerFormData(business.businessId);
      if(dataSameAsServer) {
        this.step = 5;
        this.validateBusinessOwnersForm(errors.businessOwnersForm);
      }
    } else if(errors.bankInformationForm) {
      const dataSameAsServer = await this.compareServerFormData(business.businessId);
      if(dataSameAsServer) {
        this.step = 6;
        this.validateBankInformationForm(errors.bankInformationForm);
      }
    }
  }

  async compareServerFormData(businessId: string) {
    const formData = await this.nuveiService.getApplicationForm(businessId);
    if(!deepEqual(formData.legalForm, timestampsToDate(this.legalForm.value))){
      console.log(formData.legalForm, timestampsToDate(this.legalForm.value))
    }
    if(!deepEqual(formData.dbaForm, timestampsToDate(this.dbaForm.value))){
      console.log(formData.dbaForm, timestampsToDate(this.dbaForm.value))
    }
    if(!deepEqual(formData.additionalBusinessInfoForm, timestampsToDate(this.additionalBusinessInfoForm.value))){
      console.log(formData.additionalBusinessInfoForm, timestampsToDate(this.additionalBusinessInfoForm.value))
    }
    if(!deepEqual(formData.salesProfileForm, timestampsToDate(this.salesProfileForm.value))){
      console.log(formData.salesProfileForm, timestampsToDate(this.salesProfileForm.value))
    }
    if(!deepEqual(formData.businessOwnersForm, timestampsToDate(this.businessOwnersForm.value))){
      console.log(formData.businessOwnersForm, timestampsToDate(this.businessOwnersForm.value))
    }
    if(!deepEqual(formData.bankInformationForm, timestampsToDate(this.bankInformationForm.value))){
      console.log(formData.bankInformationForm, timestampsToDate(this.bankInformationForm.value))
    }
    if(!deepEqual(formData.legalForm, timestampsToDate(this.legalForm.value))
        || !deepEqual(formData.dbaForm, timestampsToDate(this.dbaForm.value))
        || !deepEqual(formData.additionalBusinessInfoForm, timestampsToDate(this.additionalBusinessInfoForm.value))
        || !deepEqual(formData.salesProfileForm, timestampsToDate(this.salesProfileForm.value))
        || !deepEqual(formData.businessOwnersForm, timestampsToDate(this.businessOwnersForm.value))
        || !deepEqual(formData.bankInformationForm, timestampsToDate(this.bankInformationForm.value))
    ) {
      this.handleDifferentServerForm();
      return false;
    }
    return true;

  }

  handleDifferentServerForm() {
    this.modalsService.open(ConfirmationDialog, {
      behavior: ModalBehavior.Dialog,
      data: {
        title: 'Data changed',
        message: 'The information has changed. Please reload before continuing.',
        actionTitle: 'Reload',
        showCancelButton: false,
        action: () => {
          this.window.location.reload();
        },
        actionColor: 'primary'
      }
    })
  }

}
